import type { Enums, Tables } from '../../types/database.types';

export type TaskFile = Tables<'task_files'>;

export type FileCategory = Enums<'file_category'>;
export type FileReviewStatus = Enums<'file_review_status'>;
export type FileSource = Enums<'file_source'>;

/** A file row joined to its uploader's readable identity. */
export interface FileWithUploader {
  id: string;
  task_id: string | null;
  project_id: string;
  source: FileSource;
  drive_file_id: string | null;
  url: string;
  name: string;
  mime_type: string | null;
  size_bytes: number | null;
  category: FileCategory;
  uploaded_by: string;
  review_status: FileReviewStatus;
  /** Shared into the supplier review branch (handoff 29/33, Phase 6). */
  is_supplier_visible: boolean;
  asset_group_id: string | null;
  version_number: number;
  is_latest_version: boolean;
  version_note: string | null;
  superseded_at: string | null;
  created_at: string;
  /** Empty when the uploader's profile is unreadable; render a fallback. */
  uploader_name: string;
}

/**
 * A row in the cross-project Files browser (handoff 9 navigation): a latest
 * version joined to where it lives. Row Level Security decides what appears;
 * empty join fields mean the name became unreadable, not that it vanished.
 */
export interface FileBrowserItem extends FileWithUploader {
  task_title: string;
  project_name: string;
}

/** What the Files page asks the database for; filtering is server-side. */
export interface FileBrowserFilters {
  search: string;
  category: FileCategory | 'all';
  /** A project id, or 'all' for every project the caller can see. */
  projectId: string;
}

export interface AttachLinkInput {
  taskId: string;
  projectId: string;
  url: string;
  name: string;
  category: FileCategory;
}

export interface UploadFileInput {
  taskId: string;
  projectId: string;
  file: File;
  category: FileCategory;
  /** Set when this upload is a new version of an existing logical asset. */
  assetGroupId?: string;
  versionNote?: string;
}

export const FILE_CATEGORY_LABELS: Record<FileCategory, string> = {
  task_asset: 'Task asset',
  reference: 'Reference',
  deliverable: 'Deliverable',
  supplier_review: 'Supplier review',
};

export const FILE_REVIEW_STATUS_LABELS: Record<FileReviewStatus, string> = {
  none: 'Not in review',
  pending_review: 'Pending review',
  revision_required: 'Revision required',
  approved: 'Approved',
};

/**
 * The Drive file id inside a pasted Google link, or null when the link is
 * not a Drive link at all (in which case it is stored as an external link).
 * Covers files, Docs/Sheets/Slides, and the open?id= form.
 */
export function parseDriveLink(url: string): string | null {
  if (!/^https?:\/\/(?:drive|docs)\.google\.com\//i.test(url)) {
    return null;
  }
  const patterns = [
    /\/file\/d\/([\w-]{10,})/,
    /\/(?:document|spreadsheets|presentation|forms)\/d\/([\w-]{10,})/,
    /[?&]id=([\w-]{10,})/,
  ];
  for (const pattern of patterns) {
    const match = pattern.exec(url);
    if (match?.[1]) {
      return match[1];
    }
  }
  return null;
}

/** The embeddable preview page for a Drive file (images, PDFs, and more). */
export function drivePreviewUrl(driveFileId: string): string {
  return `https://drive.google.com/file/d/${driveFileId}/preview`;
}

export function isImageMime(mime: string | null): boolean {
  return mime !== null && mime.startsWith('image/');
}

export function isPdfMime(mime: string | null): boolean {
  return mime === 'application/pdf';
}

/**
 * One logical asset (handoff 30): the version shown prominently plus its
 * older versions, newest first. A standalone file is an asset with no
 * history.
 */
export interface AssetVersions {
  latest: FileWithUploader;
  history: FileWithUploader[];
}

/**
 * Folds a task's file rows (newest first) into logical assets. Rows sharing
 * an asset_group_id are versions of one asset; rows without a group stand
 * alone. The row flagged is_latest_version fronts its group; if a group has
 * no such row (its latest was removed while every survivor sat trashed),
 * the newest remaining version fronts it so nothing silently disappears.
 */
export function groupIntoAssets(files: FileWithUploader[]): AssetVersions[] {
  const groups = new Map<string, FileWithUploader[]>();
  for (const file of files) {
    const key = file.asset_group_id ?? file.id;
    const members = groups.get(key);
    if (members === undefined) {
      groups.set(key, [file]);
    } else {
      members.push(file);
    }
  }

  const assets: AssetVersions[] = [];
  for (const members of groups.values()) {
    const sorted = [...members].sort(
      (a, b) => b.version_number - a.version_number || b.created_at.localeCompare(a.created_at),
    );
    const latest = sorted.find((file) => file.is_latest_version) ?? sorted[0];
    if (latest === undefined) {
      continue;
    }
    assets.push({ latest, history: sorted.filter((file) => file.id !== latest.id) });
  }
  return assets;
}

/** A size a person can read; null sizes (links) render as nothing. */
export function formatFileSize(bytes: number | null): string {
  if (bytes === null) {
    return '';
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
